import { useEffect, useState, useCallback, useRef } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState, cacheGet } from "@/lib/swr";
import { useDash } from "./ctx";
import { toast } from "sonner";
import ConversationPanel from "./ConversationPanel";
import { Switch } from "@/components/ui/switch";
import { Headset, MessagesSquare, RefreshCw, Building2, ShieldAlert, Circle, Search, Voicemail } from "lucide-react";

const STATUS_STYLE = {
  escalated: "bg-amber-50 text-amber-700",
  human_handling: "bg-redline-50 text-redline-700",
  waiting: "bg-sky-50 text-sky-700",
  active: "bg-emerald-50 text-emerald-700",
  resolved: "bg-sand-100 text-ink-700",
  archived: "bg-sand-100 text-muted-foreground",
};

export default function OperatorConsole() {
  const { role } = useDash();
  const [data, setData] = useCachedState("op-inbox", { conversations: [], unread_total: 0 });
  const [openId, setOpenId] = useState(null);
  const [community, setCommunity] = useState("");
  const [loading, setLoading] = useState(() => cacheGet("op-inbox") === undefined);
  const [presence, setPresence] = useState({ online: false, online_count: 0 });
  const [view, setView] = useState("queue");
  const [search, setSearch] = useState("");
  const openRef = useRef(null);
  openRef.current = openId;

  const load = useCallback(async () => {
    const params = view === "all" ? `?view=all${search.trim() ? `&q=${encodeURIComponent(search.trim())}` : ""}` : "";
    const r = await api.get(`/operator/inbox${params}`);
    setData(r.data);
    setLoading(false);
    const ids = r.data.conversations.map((c) => c.id);
    setOpenId((prev) => (prev && ids.includes(prev)) ? prev : (r.data.conversations[0]?.id || null));
  }, [view, search]);

  const loadPresence = useCallback(async () => {
    try { const r = await api.get("/operator/presence"); setPresence(r.data); } catch (e) {}
  }, []);

  useEffect(() => {
    load();
    loadPresence();
    const t = setInterval(load, 5000); // near-real-time polling
    const p = setInterval(loadPresence, 15000);
    return () => { clearInterval(t); clearInterval(p); };
  }, [load, loadPresence]);

  const undoOffline = async (ids) => {
    try {
      await api.post("/operator/presence", { online: true });
      await Promise.all((ids || []).map((id) => api.post(`/conversations/${id}/claim`).catch(() => {})));
      await loadPresence();
      load();
      toast.success(`Back online — ${ids.length} conversation${ids.length === 1 ? "" : "s"} returned to you`);
    } catch (e) { toast.error("Could not undo"); }
  };

  const togglePresence = async () => {
    try {
      const r = await api.post("/operator/presence", { online: !presence.online });
      setPresence({ online: r.data.online, online_count: r.data.online_count });
      if (r.data.online) {
        toast.success("You're online — residents can reach a live operator");
      } else {
        const n = r.data.released || 0;
        const ids = r.data.released_ids || [];
        load();
        toast.success(n > 0
          ? `You're offline — ${n} conversation${n === 1 ? "" : "s"} handed off`
          : "You're offline",
          n > 0 ? { action: { label: "Undo", onClick: () => undoOffline(ids) }, duration: 8000 } : undefined);
      }
    } catch (e) { toast.error("Could not update your status"); }
  };

  const open = async (id) => {
    setOpenId(id);
    try { await api.post(`/conversations/${id}/read`); load(); } catch (e) {}
  };

  const communities = [...new Set(data.conversations.map((c) => c.tenant_name))];
  const isSafety = (c) => c.escalation_reason === "security_report" && ["waiting", "escalated", "human_handling"].includes(c.status);
  const isVoicemail = (c) => c.escalation_reason === "voicemail" && ["waiting", "escalated", "human_handling"].includes(c.status);
  const priority = (c) => (isSafety(c) ? 2 : isVoicemail(c) ? 1 : 0);
  const rows = data.conversations
    .filter((c) => !community || c.tenant_name === community)
    .sort((a, b) => priority(b) - priority(a));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <PageTitle title={<><Headset className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> Operator Desk</>} about="Redline's live queue across every community running Community Concierge or Security. Claim a thread to pause that community's AI and reply as a live operator." />
        </div>
        <div className="flex items-center gap-2">
          <div data-testid="operator-presence" className={`flex items-center gap-3 rounded-full border pl-4 pr-3 py-2 transition-colors ${presence.online ? "bg-emerald-50 border-emerald-200" : "bg-white border-sand-200"}`}>
            <span className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${presence.online ? "bg-emerald-500 animate-pulse" : "bg-sand-400"}`} />
              <span className="leading-tight">
                <span className={`block text-sm font-bold ${presence.online ? "text-emerald-700" : "text-ink"}`}>{presence.online ? "Online" : "Offline"}</span>
                <span className="block text-[10px] text-muted-foreground -mt-0.5">{presence.online ? "taking live chats" : "not taking chats"}</span>
              </span>
            </span>
            <Switch data-testid="operator-presence-toggle" checked={presence.online} onCheckedChange={togglePresence}
              className="data-[state=checked]:bg-emerald-500" aria-label="Toggle availability for live chat" />
          </div>
          <span data-testid="operator-online-count" className="text-xs font-medium text-muted-foreground bg-white border border-sand-200 rounded-full px-3 py-2">
            {presence.online_count} online
          </span>
          {data.unread_total > 0 && (
            <span data-testid="operator-unread-badge"
              className={`text-sm font-bold rounded-full px-3 py-1.5 ${presence.online ? "bg-redline-500 text-white" : "bg-amber-100 text-amber-800 border border-amber-300"}`}>
              {presence.online ? `${data.unread_total} new` : `${data.unread_total} waiting`}
            </span>
          )}
          <button data-testid="operator-refresh" onClick={load} className="h-9 w-9 rounded-full bg-white border border-sand-200 flex items-center justify-center hover:border-ink transition-colors">
            <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {!presence.online && (
        <div data-testid="operator-offline-banner" className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 shrink-0" /> You're offline. Residents asking for a live person will be told the team is unavailable and their thread will wait here until an operator goes online and claims it.
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex rounded-full border border-sand-200 bg-white p-1">
          <button data-testid="operator-view-queue" onClick={() => { setView("queue"); setSearch(""); }}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${view === "queue" ? "bg-ink text-white" : "text-ink-700 hover:bg-sand-50"}`}>
            Live queue
          </button>
          <button data-testid="operator-view-history" onClick={() => setView("all")}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${view === "all" ? "bg-ink text-white" : "text-ink-700 hover:bg-sand-50"}`}>
            History
          </button>
        </div>
        {view === "all" && (
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input data-testid="operator-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or phone…"
              className="w-full rounded-full bg-white border border-sand-200 pl-9 pr-4 py-2 text-sm outline-none focus:border-ink" />
          </div>
        )}
        {view === "all" && (
          <span className="text-xs text-muted-foreground">Every conversation — including ones resolved or handed back to the AI. Open one and reopen it to take over again.</span>
        )}
      </div>


      {communities.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setCommunity("")} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${!community ? "bg-ink text-white" : "bg-white border border-sand-200 hover:border-ink"}`}>All communities</button>
          {communities.map((c) => (
            <button key={c} data-testid={`operator-community-${c}`} onClick={() => setCommunity(c)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${community === c ? "bg-ink text-white" : "bg-white border border-sand-200 hover:border-ink"}`}>
              <Building2 className="h-3.5 w-3.5" /> {c}
            </button>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-[minmax(300px,380px)_1fr] gap-5 items-start">
        <div className="bg-white rounded-2xl border border-sand-200 divide-y divide-sand-200 overflow-hidden max-h-[72vh] overflow-y-auto chat-scroll">
          {!loading && rows.length === 0 && (
            <div className="p-10 text-center text-muted-foreground flex flex-col items-center gap-2">
              <MessagesSquare className="h-8 w-8 text-sand-300" />
              {view === "all" ? (search.trim() ? "No conversations match that search." : "No conversations yet.") : "Inbox is clear — nothing needs a human right now."}
            </div>
          )}
          {rows.map((c) => (
            <button key={c.id} data-testid={`operator-row-${c.id}`} onClick={() => open(c.id)}
              className={`w-full flex items-center gap-3 p-4 text-left transition-colors ${openId === c.id ? "bg-redline-50" : "hover:bg-sand-50"} ${isSafety(c) ? "border-l-4 border-redline-500" : isVoicemail(c) ? "border-l-4 border-amber-500" : ""}`}>
              <div className="relative h-10 w-10 rounded-full bg-redline-50 text-redline-600 flex items-center justify-center font-bold shrink-0">
                {(c.active_person_name || "?")[0]}
                {c.unread && <span data-testid={`operator-unread-${c.id}`} className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-redline-500 border-2 border-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold truncate">{c.active_person_name || "Unknown"}</span>
                  {isSafety(c) && (
                    <span data-testid={`operator-safety-${c.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-redline-500 text-white shrink-0">
                      <ShieldAlert className="h-2.5 w-2.5" strokeWidth={2.5} /> SAFETY
                    </span>
                  )}
                  {isVoicemail(c) && (
                    <span data-testid={`operator-voicemail-${c.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white shrink-0">
                      <Voicemail className="h-2.5 w-2.5" strokeWidth={2.5} /> VOICEMAIL
                    </span>
                  )}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize shrink-0 ${STATUS_STYLE[c.status] || "bg-sand-100 text-ink-700"}`}>{(c.status || "").replace(/_/g, " ")}</span>
                </div>
                <div className="text-[11px] text-redline-600 font-medium flex items-center gap-1 truncate"><Building2 className="h-3 w-3 shrink-0" /> {c.tenant_name}</div>
                <div className="text-sm text-muted-foreground truncate">{c.last_message}</div>
                {c.owned_by && <div className="text-[10px] text-ink-700 mt-0.5">Handled by {c.owned_by}</div>}
              </div>
            </button>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden h-[72vh]">
          <ConversationPanel convId={openId} role={role} onChanged={() => { load(); loadPresence(); }} operator />
        </div>
      </div>
    </div>
  );
}
